import { ArrowLeft, Clock, Croissant, Route as RouteIcon, Bike } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { BakeryCrawlCard } from "@/components/BakeryCrawl";
import { DayMiniMap } from "@/components/DayMiniMap";
import { GygTourBand } from "@/components/GetYourGuide";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ShareBar } from "@/components/ShareBar";
import {
  BAKERY_CRAWL_ROUTE,
  BAKERY_CRAWL_ROUTE_2,
  BAKERY_CRAWL_ITEMLIST,
  BAKERY_CRAWL_ITEMLIST_2,
} from "@/data/bakeryCrawl";

const HERO_IMG =
  "https://images.unsplash.com/photo-1534432182912-63863115e106?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&h=900&fit=crop";

const TIPS = [
  {
    icon: Clock,
    title: "Go early",
    body: "The good buns sell out — the famous places are often down to the plain ones by early afternoon. Start when the shutters come up.",
  },
  {
    icon: Croissant,
    title: "The benchmark is the cardamom bun",
    body: "Every bakery on these routes is judged on its kardemommesnurre. Try one at the first stop and you'll have a scale for the rest of the day.",
  },
  {
    icon: Bike,
    title: "Route two could be a bike day",
    body: "Fourteen kilometres with a harbour crossing and a Refshaleøen finale — lovely by bike, a long one on foot. Route 1 is the easy walker.",
  },
];

// Page FAQ — answers stay plain strings so the FAQPage schema below reuses them.
const FAQS = [
  {
    q: "How long does the Copenhagen bakery crawl take?",
    a: "Route 1 is about 6 km with 8 bakeries — a leisurely morning of three to four hours with stops. Route 2 is about 14 km with 7 bakeries, so plan half a day and seriously consider a bike.",
  },
  {
    q: "Where does the Copenhagen bakery crawl start?",
    a: "Route 1 starts at Bosses Bager on Østerbrogade in Østerbro and ends at Sankt Peders Bageri in the Latin Quarter. Route 2 starts at Københavns Bageri in Carlsberg Byen and ends at Lille Bakery out on Refshaleøen.",
  },
  {
    q: "Can I get the bakery crawl route on Google Maps?",
    a: "Yes — hit the 'Open the whole route in Google Maps' button under each route and it opens in Google Maps with every bakery as a waypoint. Dropped a stop from the list? The link follows your remaining stops automatically.",
  },
  {
    q: "Can you do the Copenhagen bakery crawl by bike?",
    a: "Route 2 is made for it — 14 km with a harbour crossing and a Refshaleøen finale is lovely on two wheels but a long day on foot. Route 1 is the easy walker through the old town.",
  },
  {
    q: "How much does the bakery crawl cost?",
    a: "The route itself is free — you pay per pastry, roughly 30–50 kr. each at the good places. Share everything between two and the whole day stays very reasonable.",
  },
  {
    q: "What is the best time to do the bakery crawl?",
    a: "Start when the shutters come up — the famous buns sell out, and the popular places are often down to the plain ones by early afternoon. Weekday mornings are the calmest; weekend queues peak between 9 and 11.",
  },
  {
    q: "Is the bakery crawl worth doing in fastelavnsbolle season?",
    a: "It's the best time of year to do it. From early January until Fastelavn — Denmark's Shrovetide, in February or early March — every bakery on the routes bakes its own fastelavnsbolle, the cream-filled Shrovetide bun, and they all compete to make the best one. Crawl a few stops, split one at each and crown your own winner. After Fastelavn they're gone for the year.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function BakeryCrawlPage() {
  return (
    <main data-testid="bakery-crawl-page" className="pt-16 md:pt-20">
      <Seo
        title="The Copenhagen Bakery Crawl — Two Ready-Made Routes"
        description="Two ready-made bakery crawls through Copenhagen: the classic 6 km old-town walk and the 14 km neighbourhood route to Refshaleøen. Fifteen bakeries, one address each."
        path="/bakery-crawl"
        image={HERO_IMG}
      />
      <PageSchema
        name="The Copenhagen bakery crawl — two ready-made walking routes"
        description="Two ready-made bakery routes through Copenhagen: the classic old-town crawl and the longer neighbourhood crawl to Refshaleøen."
        path="/bakery-crawl"
        crumb="The bakery crawl"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BAKERY_CRAWL_ITEMLIST)}</script>
        <script type="application/ld+json">{JSON.stringify(BAKERY_CRAWL_ITEMLIST_2)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Danish pastries and a crumb cake on display at a Copenhagen bakery"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-16 pb-12 md:pt-28 md:pb-16">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/bakeries"
              data-testid="bakery-crawl-back"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-black hover:bg-white hover:text-black transition-colors"
            >
              <ArrowLeft size={16} /> The bakery guide
            </Link>
            <ShareButton testid="bakery-crawl-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">
              TWO ROUTES · FIFTEEN BAKERIES
            </p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            The Copenhagen<br />
            <span className="font-editorial italic font-600">bakery crawl</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/85 max-w-2xl mt-6 leading-relaxed">
            Two ready-made routes, one address per brand. Walk the classic through the old town —
            or take the long way out through the neighbourhoods to Refshaleøen.
          </p>
          <div className="flex flex-wrap gap-2.5 mt-6">
            {["Route 1 · about 6 km", "Route 2 · about 14 km", "8 + 7 bakeries", "Route 2 is best by bike"].map((t) => (
              <span
                key={t}
                data-testid={`bakery-crawl-fact-${t.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                className="inline-flex items-center gap-1.5 font-body font-700 text-xs bg-black/5 border border-black/15 rounded-full px-3.5 py-1.5 backdrop-blur-sm"
              >
                <Croissant size={12} className="text-[#DF3226]" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* route 1 — the classic */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <div data-testid="bakery-route-1-map" className="mb-6">
            <DayMiniMap items={BAKERY_CRAWL_ROUTE} heightClass="h-72 md:h-96" />
          </div>
          <BakeryCrawlCard
            kicker="ROUTE 1 · ØSTERBRO TO THE LATIN QUARTER"
            heading={
              <>
                Route 1:{" "}
                <span className="font-editorial italic font-600">the classic</span>
              </>
            }
            idPrefix="bakery-route"
          />
        </div>
      </section>

      {/* route 2 — the neighbourhoods */}
      <section className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <div data-testid="bakery-route-2-map" className="mb-6">
            <DayMiniMap items={BAKERY_CRAWL_ROUTE_2} heightClass="h-72 md:h-96" />
          </div>
          <BakeryCrawlCard
            route={BAKERY_CRAWL_ROUTE_2}
            kicker="ROUTE 2 · CARLSBERG BYEN TO REFSHALEØEN"
            heading={
              <>
                Route 2:{" "}
                <span className="font-editorial italic font-600">the neighbourhoods</span>
              </>
            }
            copy="The four we cut from the classic, plus three more — Carlsberg Byen and Vesterbro, over the harbour to Islands Brygge and Amager, ending out on Refshaleøen. About 14 km — honestly better by bike."
            idPrefix="bakery-route-2"
          />
        </div>
      </section>

      {/* good to know */}
      <section className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">
              GOOD TO KNOW
            </p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-4xl">
              Crawl it like a local
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {TIPS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06} y={30}>
                <div
                  data-testid={`bakery-crawl-tip-${i}`}
                  className="bg-[#F9F8F6] border border-zinc-200 rounded-2xl p-6 h-full"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#DF3226] text-white shrink-0 mb-4">
                    <t.icon size={18} />
                  </div>
                  <h3 className="font-display font-900 tracking-tight text-xl leading-none">
                    {t.title}
                  </h3>
                  <p className="font-body text-sm text-black/60 mt-2 leading-relaxed">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-10">
              <Link
                to="/bakeries"
                data-testid="bakery-crawl-to-bakeries"
                className="inline-flex items-center gap-1 font-body font-800 text-sm text-[#DF3226] hover:text-black transition-colors"
              >
                The full bakery guide
              </Link>
              <Link
                to="/planner"
                data-testid="bakery-crawl-to-planner"
                className="inline-flex items-center gap-1 font-body font-800 text-sm text-[#DF3226] hover:text-black transition-colors"
              >
                Build it into your itinerary
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* bookable tour — private Danish pastry tasting */}
      <GygTourBand tourId="621540" label="RATHER TASTE WITH A LOCAL? BOOK A TOUR" />

      <ExploreLinks
        testid="explore-bakery-crawl"
        links={[
          { label: "Every great bakery, mapped — the bakery guide", to: "/bakeries" },
          { label: "What else to eat — the Danish food guide", to: "/danish-food" },
          { label: "Lunch after the sugar — smørrebrød, ranked", to: "/smorrebrod" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="bakery-crawl-faq" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter text-3xl md:text-4xl mb-8">
              Bakery crawl questions, <span className="font-editorial italic font-600">answered</span>
            </h2>
          </Reveal>
          <div className="divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`bakery-crawl-faq-${i}`} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer font-display font-900 text-lg md:text-xl tracking-tight list-none">
                  {f.q}
                  <span className="font-body text-[#DF3226] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-3 max-w-2xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="The Copenhagen Bakery Crawl" />
    </main>
  );
}
